// (C) 2022 GoodData Corporation
import { FormatChar } from "./fmt_number_commons.js";
import { ResultColors } from "./gooddata_format.js";
import { extractColorCoding } from "./fmt_base.js";
/**
 * State machine looking for indication whether number should have thousands separated. This is
 * equivalent to regex '[0#?],[0#?]' used here:
 *
 * https://github.com/gooddata/gdc-numberjs/blob/0ab90ee596a854b8ad38fa7956d7148b8c1291df/lib/number.js#L462
 */
export class ThousandSeparatorMatcher {
    constructor() {
        this.state = "Null";
    }
    onCharacter(fc) {
        switch (this.state) {
            case "Matched":
                break;
            case "Null":
                if (fc.isPlacement()) {
                    this.state = "HasPlacementChar";
                }
                break;
            case "HasPlacementChar":
                if (fc.isPlacement()) {
                    this.state = "HasPlacementChar";
                }
                else if (fc.isComma()) {
                    this.state = "HasComma";
                }
                else {
                    this.state = "Null";
                }
                break;
            case "HasComma":
                if (fc.isPlacement()) {
                    this.state = "Matched";
                }
                else {
                    this.state = "Null";
                }
                break;
        }
    }
    isMatched() {
        return this.state === "Matched";
    }
}
/**
 *  Digit counter machine. Counts the number of placement characters in the format string.
 */
export class DigitCounter {
    constructor(fractional_part = false) {
        this.counter = 0;
        this.state = fractional_part ? "ExpectingAnyPlacement" : "WaitingForZero";
    }
    onCharacter(fc) {
        switch (this.state) {
            case "Done":
                break;
            case "ExpectingAnyPlacement":
                if (fc.isPlacement()) {
                    this.state = "CountingDigits";
                    this.counter++;
                }
                else {
                    this.state = "Done";
                }
                break;
            case "WaitingForZero":
                if (fc.isZeroPlacement()) {
                    this.state = "CountingDigits";
                    this.counter++;
                }
                break;
            case "CountingDigits":
                if (fc.isPlacement()) {
                    this.counter++;
                }
                else {
                    this.state = "Done";
                }
                break;
        }
    }
    count() {
        return this.counter;
    }
}
/**
 * Value scaling counter machine. Counts the number of commas in the format string.
 */
export class ValueScalingCounter {
    constructor(include_null_state = false) {
        this.scalingValue = 0;
        const s = include_null_state ? "Null" : "CommasExpected";
        this.initialState = s;
        this.state = s;
    }
    onCharacter(fc) {
        switch (this.state) {
            case "Null":
                if (fc.isPlacement()) {
                    this.state = "CommasExpected";
                }
                break;
            case "CommasExpected":
                if (fc.isComma()) {
                    this.state = "CollectScaling";
                    this.scalingValue++;
                }
                else if (!fc.isPlacement()) {
                    // possibly reset to 'Null' state
                    this.state = this.initialState;
                    this.scalingValue = 0;
                }
                break;
            case "CollectScaling":
                if (fc.isComma()) {
                    this.scalingValue++;
                }
                else if (fc.isPlacement()) {
                    // this is to match what original code does
                    // if the commas are followed by a placement char (e.g. ,,#) then scaling will be number of commas - 1
                    // if the commas are followed by any other char or are at the end of the format pattern,
                    //  then scaling is equal to number of consecutive commas
                    //
                    // see desc for state machine, original patterns are mentioned there
                    this.scalingValue--;
                    // if any value scaling was identified, move to Done and do not do any more work
                    // otherwise, reset back to expecting commas
                    this.state = this.scalingValue ? "Done" : "CommasExpected";
                }
                else {
                    this.state = "Done";
                }
                break;
            case "Done":
                break;
        }
    }
    scaling() {
        return this.scalingValue;
    }
}
/**
 *  Visuals analysis result.
 */
export class VisualsAnalysisResult {
    constructor() {
        this.visualPart = "";
        this.bareFormatWithPlaceholders = "";
        this.colorPlaceholderValues = [];
        this.colors = new ResultColors();
    }
}
export function analyzeVisualTags(matchers, formatString) {
    const colorMatcher = matchers.getColorMatcher();
    const customColorMatcher = matchers.getCustomColorMatcher();
    const customBgColorMatcher = matchers.getCustomBgColorMatcher();
    const visualMatcher = matchers.getAnyCustomColorMatcher();
    const result = new VisualsAnalysisResult();
    /*
     * Step 1: separate visual part from the bare part; this follows the reference impl. First-found sequence of
     * custom colors is treated as visual part; the format string that does not contain the visual part is considered
     * the bare part.
     *
     * Note that in some funkier format strings, the bare part may still contain color coding tags
     */
    let bareFormat = formatString;
    result.colors = extractColorCoding(colorMatcher, customColorMatcher, customBgColorMatcher, formatString);
    visualMatcher.lastIndex = 0;
    if (visualMatcher.test(bareFormat)) {
        const visualMatch = bareFormat.match(visualMatcher);
        if (visualMatch) {
            const visualStart = bareFormat.search(visualMatcher);
            const visualEnd = visualStart + visualMatch[0].length;
            result.visualPart = visualMatch[0];
            // this mimics reference implementation; while it handles color tags differently, through happy
            // coincidences (hash is treated as optional digit placeholder - if no number to place then the hash is
            // essentially removed) the resulting formatted value will never contain hashes
            //
            // this code does the same, except at pattern compile-time
            result.visualPart = result.visualPart.replace(/#/g, ""); //replace all # with empty string
            bareFormat = bareFormat.substring(0, visualStart) + bareFormat.substring(visualEnd);
        }
    }
    /*
     * Step 2: construct bare format with color tags replaced by placeholders; hold onto values of color tags
     *
     * The bare format with placeholders is what should be later used to compile the actual format string. This is
     * because the visual tags (or in this case their single-char placeholders) need to be considered during compilation
     * as their presence can impact the compiled result.
     */
    result.bareFormatWithPlaceholders = bareFormat;
    // consider all types of color tags
    const maskingMatchers = [colorMatcher, customColorMatcher, customBgColorMatcher];
    for (const matcher of maskingMatchers) {
        // keep looking for visual tag matches; for each found match:
        //
        // 1. sequentially assign visual tag placeholder (unicode char from private use area)
        // 2. store value for the visual tag placeholder
        // 3. mutate bareFormat: replace the tag with placeholder
        //
        // This is not the most efficient approach (especially in worst-case scenarios) - however since it is done at
        // compile time, the cost is incurred once so going dirty.
        /*eslint no-constant-condition: ["error", { "checkLoops": false }]*/
        while (true) {
            matcher.lastIndex = 0;
            if (!matcher.test(result.bareFormatWithPlaceholders)) {
                break;
            }
            const colorPlaceholderStartCodePoint = FormatChar.ColorPlaceholderStart.codePointAt(0);
            const colorPlaceholderEndCodePoint = FormatChar.ColorPlaceholderEnd.codePointAt(0);
            if (colorPlaceholderStartCodePoint === undefined || colorPlaceholderEndCodePoint === undefined) {
                throw new Error("Unexpected format characters.");
            }
            const visualPlaceholder = colorPlaceholderStartCodePoint + result.colorPlaceholderValues.length;
            if (visualPlaceholder > colorPlaceholderEndCodePoint) {
                throw new Error("Too many visual tags. You can specify at most 256 tags.");
            }
            const tagMatch = result.bareFormatWithPlaceholders.match(matcher);
            if (tagMatch) {
                const tagStart = result.bareFormatWithPlaceholders.search(matcher);
                const tagEnd = tagStart + tagMatch[0].length;
                let tag = tagMatch[0];
                tag = tag.replace(/#/g, ""); //replace all
                result.colorPlaceholderValues.push(tag);
                result.bareFormatWithPlaceholders =
                    result.bareFormatWithPlaceholders.substring(0, tagStart) +
                        String.fromCodePoint(visualPlaceholder) +
                        result.bareFormatWithPlaceholders.substring(tagEnd);
            }
        }
    }
    return result;
}
//# sourceMappingURL=fmt_number_compile.js.map